import React from 'react';
import clsx from 'clsx';
import Anchor from '@/components/ui/Anchor/Anchor';

interface FroalaDeletePopoverProps {
  deleteIcon: string | React.ReactNode;
  onDelete: () => void;
  targetElement: HTMLElement;
  editorTop: number;
  editorInstance: any;
  width?: string;
  height?: string;
  offset?: { top?: number; left?: number };
  position?: 'bottom-left' | 'top-right';
  showCaret?: boolean;
}

function FroalaDeletePopover({
  deleteIcon,
  onDelete,
  targetElement,
  editorTop,
  editorInstance,
  width = 'tw-w-20',
  height = 'tw-h-10',
  offset = { top: 50 },
  position,
  showCaret = true,
}: FroalaDeletePopoverProps) {
  // Check if popup would be obstructed by toolbar
  // Use the top offset value to determine obstruction threshold
  const obstructionThreshold = Math.abs(offset.top || 50);
  const wouldBeObstructed = targetElement.getBoundingClientRect().top - editorTop < obstructionThreshold;

  const handleDelete = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!editorInstance) { return; }

    editorInstance.undo.saveStep();
    onDelete();

    editorInstance.undo.saveStep();
    editorInstance.events.trigger('contentChanged');
  };

  // Render icon based on type
  const renderIcon = () => {
    const baseProps = {
      className: `tw-absolute tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center
                  tw-border-0 tw-bg-transparent tw-cursor-pointer`,
      onMouseDown: handleDelete,
    };

    if (typeof deleteIcon === 'string') {
      return (
        <button
          dangerouslySetInnerHTML={{ __html: deleteIcon }}
          type="button"
          {...baseProps}
        />
      );
    }

    return (
      <button type="button" {...baseProps}>
        {deleteIcon}
      </button>
    );
  };

  const caretClasses = {
    bottomTop: `tw-left-1/2 tw--ml-1.5 tw-border-l-[6px] tw-border-r-[6px]
                tw-border-l-transparent tw-border-r-transparent`,
    right: `tw-left-[-6px] tw-top-1/2 tw--mt-1.5 tw-border-t-[6px] tw-border-b-[6px] tw-border-r-[6px]
            tw-border-t-transparent tw-border-b-transparent tw-border-r-white`,
  };
  const positionedRight = position?.includes('-right');

  return (
    <Anchor
      attachListenersTo={[document.querySelector('.fr-wrapper')]}
      className={clsx(
        width,
        height,
        'tw-bg-white tw-rounded',
        {
          'tw-shadow-[0_-2px_7px_rgba(0,0,0,0.1),0_2px_10px_rgba(0,0,0,0.15)]': wouldBeObstructed,
          'tw-shadow-lg': !wouldBeObstructed,
        },
      )}
      offset={offset}
      position={position}
      to={targetElement}
    >
      {renderIcon()}
      {showCaret && (
        <div className={clsx('tw-absolute tw-w-0 tw-h-0 tw-border-solid', {
          // Right-positioned caret (points left)
          [caretClasses.right]: positionedRight,
          // Default bottom/top positioned caret (points up/down)
          [caretClasses.bottomTop]: !positionedRight,
          // Normal (points up)
          'tw-bottom-[-6px] tw-border-t-[6px] tw-border-t-white': !positionedRight && !wouldBeObstructed,
          // Obstructed (points down)
          'tw-top-[-6px] tw-border-b-[6px] tw-border-b-white': !positionedRight && wouldBeObstructed,
        })}
        />
      )}
    </Anchor>
  );
}

export default FroalaDeletePopover;
